<template>
  <div>
    <div
      v-if="orderedSection?.length > 0"
      class="pb-2"
    >
      <section v-if="sectionLogo">
        <div class="justify-left mb-5 flex items-center">
          <img
            :src="sectionLogo.url"
            :alt="sectionLogo.filename"
            class="h-11 w-auto"
          />
        </div>
      </section>
      <section
        v-if="headerInfo.enable"
        class="w-full text-left"
      >
        <h4
          v-if="headerInfo.title"
          class="mb-3 text-base"
          v-html="headerInfo.title"
        />

        <p
          v-if="headerInfo.text"
          v-html="headerInfo.text"
        />
      </section>
      <div
        v-for="(section, index) in orderedSection"
        :key="index"
      >
        <div
          v-for="(question, index) in order(section.company_questions, 'order')"
          v-show="sectionOrder[currentSectionIndex] === section.order"
          :key="index"
          class="text-left"
        >
          <dynamic-question-form
            :item="question"
            :toggle-required="toggleRequired"
            :hidden="hidden"
            @hide="hideHidden"
            @unhide="unhideHidden"
          />
        </div>
      </div>
      <section
        v-if="footerInfo.enable"
        class="w-full text-left"
      >
        <h4
          v-if="footerInfo.title"
          class="mb-3 text-base"
          v-html="footerInfo.title"
        />

        <p
          v-if="footerInfo.text"
          v-html="footerInfo.text"
        />
      </section>
    </div>
    <div v-else>
      <div
        class="rounded-1 mb-2 mt-4 w-full pb-1 text-left text-sm text-body-color"
      >
        No additional information is required at this time. Please click Save &
        Continue below to proceed to the next step.
      </div>
    </div>
    <form-navigation-buttons
      v-if="!isNavigationDisabled"
      :enabled="!isNavigationDisabled"
      :component-identifier="componentIdentifier"
      :buttons-to-display="buttonsToDisplay"
      :is-button-disabled="isButtonDisabled"
      @next="handleNext"
      @back="handleBack"
      @exit-modal="emitExitModal"
    />
  </div>
</template>

<script>
import FormNavigationButtons from '@/components/FormNavigationButtons.vue';
import { useFormRouter } from '@/composables/formRouter';
import { useStore } from 'vuex';
import { computed, watch } from 'vue';
import DynamicQuestionForm from './DynamicQuestionForm.vue';
import _ from 'lodash';

export default {
  name: 'Questions',
  components: {
    FormNavigationButtons,
    DynamicQuestionForm,
  },
  props: {
    data: Object,
    formPosition: Number,
    title: String,
    updateField: Function,
    nextStep: Function,
    stepName: String,
    skipStep: Function,
    screenSize: String,
  },
  setup(_, { emit }) {
    const store = useStore();
    const componentIdentifier = 'Questions';
    const formRouter = useFormRouter(componentIdentifier);
    const isNavigationDisabled = computed(() => formRouter.isDisabled.value);
    const firstTime = computed(
      () =>
        store.state.firstTime?.[
          formRouter.componentStep.value?.firstTimeIdentifier
        ]
    );
    const buttonsToDisplay = 'continue-save';
    const isButtonDisabled = false;

    const currentSection = computed(() => store.state.questions?.section);
    const currentSectionIndex = computed(
      () => store.state.questions?.section_index
    );
    const sectionOrder = computed(() => store.state.questions?.section_order);
    const totalSections = computed(() => store.state.questions?.total_sections);

    const auto_skip_stage_six = computed(
      () => store.state.carriers.carrierData?.auto_skip_stage_six || false
    );

    const checkIsLastSection = async () => {
      const initialCurrentSection = currentSection.value;
      const { onboardingPacketBody } = store.state.onboardingPacket;
      const isLastSection =
        currentSectionIndex.value >= totalSections.value - 1;

      if (onboardingPacketBody?.onboarding_packet) {
        const { status } = await store.dispatch(
          'onboardingPacket/updatePacket',
          {
            id: store.state.onboardingPacket?.onboarding_id,
            body: { ...onboardingPacketBody },
          }
        );
        if ([200, 201, 204].includes(status)) {
          store.commit(
            'addFormDebugMessage',
            `${componentIdentifier}: Answers for section ${initialCurrentSection} guardadas (res: ${status})`
          );
        } else {
          store.commit(
            'addFormDebugMessage',
            `ERROR | ${componentIdentifier}: Falló guardado section ${initialCurrentSection} (res: ${status})`
          );
        }
      }

      if (!isLastSection) {
        const nextIdx = currentSectionIndex.value + 1;
        store.commit('questions/setSectionIndex', nextIdx);
        store.commit('questions/setSection', sectionOrder.value[nextIdx]);
        window.scrollTo(0, 0);
      }

      return isLastSection;
    };

    const handleNext = async () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleNext' called"
      );
      if (isNavigationDisabled.value)
        return store.commit(
          'addFormDebugMessage',
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      const check = await checkIsLastSection();
      console.log('check == ');
      console.dir(check);
      console.log('check == true');
      console.log(check == true);
      if (check == true) {
        store.commit(
          'addFormDebugMessage',
          componentIdentifier + ': Company Question Sections completed'
        );
        formRouter.moveToNextStep();
      } else {
        store.commit(
          'addFormDebugMessage',
          componentIdentifier +
            ': Company Question Sections not yet completed, moving to next section (' +
            currentSection.value +
            ')'
        );
      }
    };

    // backChecks: Checks to be reviewed when the user clicks the Back button (but before any store.state.formRoutes.activeStep changes)
    const backCheck1 = computed(
      () =>
        store.state.questions.section_index === 0 && auto_skip_stage_six.value
    );
    const backCheck2 = computed(
      () =>
        store.state.questions.section_index === 0 &&
        (store.state.payments.payment_type === 'factoring' ||
          !!store.state.carriers.carrierData?.payment_method)
    );
    const backCheck3 = computed(
      () => store.state.questions.section_index === 0
    );
    const backCheck4 = computed(() => store.state.questions.section_index > 0);

    const handleBack = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleBack' called"
      );
      if (isNavigationDisabled.value)
        return console.log(
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );

      if (backCheck1.value) {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck1 is TRUE (auto_skip_stages: 6 is true), skipping Payment steps and moving to Insurance-4'
        );
        store.commit('toggleDisabledButton', {
          bool: false,
          source: 'QuestionsStepOne #4',
        });
        return formRouter.moveToStepByIdentifier('Insurance-4');
      } else {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck1 is FALSE (Not skipping Payment steps)'
        );
      }

      if (backCheck2.value) {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck2 is TRUE (either payment_type is factoring or a payment_method exists), skipping Questions and moving to Payment-2'
        );
        store.commit('toggleDisabledButton', {
          bool: false,
          source: 'QuestionsStepOne #5',
        });
        return formRouter.moveToStepByIdentifier(
          isCHRControllingManagerEnabled.value ? 'Payment-5' : 'Payment-2'
        );
      } else {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck2 is FALSE (not auto-skipping Questions to Payment-2)'
        );
      }

      if (backCheck3.value) {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck3 is TRUE, moving to Payment-2'
        );
        store.commit('toggleDisabledButton', {
          bool: false,
          source: 'QuestionsStepOne #6',
        });
        return formRouter.moveToStepByIdentifier('Payment-2');
      } else {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' + componentIdentifier + ': backCheck3 is FALSE'
        );
      }

      if (backCheck4.value) {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck4 is TRUE (questions.section_index > 0), decrementing questions.section_index by 1'
        );
        store.commit('questions/setSection', store.state.questions.section - 1);
        store.commit(
          'questions/setSectionIndex',
          store.state.questions.section_index - 1
        );
        return;
      } else {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck4 is FALSE (questions.section_index is not greater than 0)'
        );
      }

      formRouter.moveToPreviousStep();
    };

    const redirectCheck = computed(() => {
      // auto_skip_stage_seven Redirect Check
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ': Redirect check (auto_skip_stage_seven)'
      );
      if (store.state.carriers?.carrierData?.auto_skip_stage_seven) {
        store.commit(
          'addFormDebugMessage',
          'REDIRECT | ' +
            componentIdentifier +
            ': Redirect needed from check (auto_skip_stage_seven), redirecting to Integrations'
        );
        return 'Integrations';
      }
      return false;
    });

    const shouldEnableCHRFlow = computed(
      () => store.getters.shouldEnableCHRFlow
    );

    const isCHRControllingManagerEnabled = computed(() => {
      const { currentUser, featureFlags } = store.state.users;
      const { carrierData } = store.state.carriers;
      console.log('CarrierData', carrierData);

      return (
        currentUser.scopes.includes('manage_users') &&
        featureFlags?.enable_chr_onboarding?.enabled &&
        shouldEnableCHRFlow.value &&
        carrierData?.payment_method?.is_type == 'load_pay'
      );
    });

    watch(isNavigationDisabled, (val) => {
      if (val == false) {
        store.commit(
          'addFormDebugMessage',
          'CHECK | ' +
            componentIdentifier +
            ': (isNavigationDisabled watcher) ' +
            componentIdentifier +
            ' is active step, running redirect checks'
        );
        if (redirectCheck.value !== false) {
          handleRedirect(redirectCheck.value);
        }
      }
    });

    const handleRedirect = (val) => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleRedirect' called"
      );
      if (isNavigationDisabled.value)
        return store.commit(
          'addFormDebugMessage',
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      formRouter.moveToStepByIdentifier(val);
    };

    const emitExitModal = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'emitExitModal' called"
      );
      emit('exit-modal');
    };

    return {
      componentIdentifier,
      handleNext,
      handleBack,
      buttonsToDisplay,
      isButtonDisabled,
      isNavigationDisabled,
      firstTime,
      emitExitModal,
    };
  },
  data() {
    return {
      required: {},
      hidden: [],
      init_hidden: false,
    };
  },
  computed: {
    currentSectionInfo() {
      return (
        this.orderedSection.find((el) => el.order === this.currentSection) ||
        null
      );
    },
    sectionLogo() {
      return this.currentSectionInfo?.logo;
    },
    headerInfo() {
      const section = this.currentSectionInfo;
      if (!section) return { enable: false, title: null, text: null };

      const { header_text = '', header_title = '' } = section;
      return {
        enable: !!(header_text || header_title),
        title: header_title,
        text: header_text,
      };
    },
    footerInfo() {
      const section = this.currentSectionInfo;
      if (!section) return { enable: false, title: null, text: null };

      const { footer_text = '', footer_title = {} } = section;
      return {
        enable: !!(footer_text || footer_title?.url),
        title: footer_title,
        text: footer_text,
      };
    },
    paymentType() {
      return this.$store.state.payments.payment_type;
    },
    orderedSection() {
      return _.orderBy(this.data.company_question_sections, 'order');
    },
    currentSection() {
      return this.$store.state.questions.section;
    },
    currentSectionIndex() {
      return this.$store.state.questions.section_index;
    },
    sectionOrder() {
      return this.$store.state.questions.section_order;
    },
    requiredCompleted() {
      if (
        this.required &&
        Object.values(this.required).length > 0 &&
        this.sectionOrder?.length
      ) {
        console.log('(Questions) this.required', this.required);
        return this.stepName === 'Questions'
          ? !Object.values(
              this.required[this.sectionOrder[this.currentSectionIndex]]
            ).some((el) => !el)
          : false;
      } else {
        return true;
      }
      // return true;
    },
    hasAnswers() {
      return this.$store.state.questions?.has_answers;
    },
  },
  watch: {
    stepName: function (val) {
      if (val === 'Questions') {
        //  if(val !== oldValue && this.paymentType === "factoring") {
        //   this.$store.dispatch('factors/getLatestFactor');
        // } else if (val !== oldValue && this.paymentType !== "factoring") {
        //   this.$store.dispatch("payments/getLatestPaymentMethod");
        // }
        if (
          this.data?.company_question_sections?.length !=
          this.$store.state.questions.total_sections
        ) {
          this.$store.commit(
            'addFormDebugMessage',
            'Questions: (stepName watcher) Updating questions.total_sections to ' +
              this.data?.company_question_sections?.length +
              ' from ' +
              this.$store.state.questions.total_sections
          );
          this.$store.commit(
            'questions/setTotalSections',
            this.data?.company_question_sections?.length
          );
        }
        // this.$store.commit(
        //   "questions/setSection",
        //   this.data.company_question_sections.length
        // );
        let sectionNames = {};
        this.data.company_question_sections.forEach((el) => {
          sectionNames[el.order] = el.name;
        });
        this.$store.commit('questions/setSectionName', sectionNames);
        this.$store.commit('toggleDisabledButton', {
          bool: true,
          source: 'QuestionsStepOne #1',
        });
        let required = {};
        let section_order = [];
        this.data.company_question_sections.forEach((section) => {
          let requiredQuestions = {};
          section.company_questions.forEach((company_question) => {
            if (
              company_question.is_required &&
              company_question?.question?.id > 0
            ) {
              if (
                company_question?.question?.most_recent_previous_answer ||
                company_question?.question?.current_answer
              ) {
                if (!company_question?.question?.is_type?.includes('file')) {
                  requiredQuestions[company_question.id] = true;
                } else {
                  let prev =
                    company_question?.question?.most_recent_previous_answer;
                  let curr = company_question?.question?.current_answer;
                  if (
                    prev?.file_uploads?.length > 0 ||
                    curr?.file_uploads?.length > 0
                  ) {
                    requiredQuestions[company_question.id] = true;
                  } else {
                    requiredQuestions[company_question.id] = false;
                  }
                }
              } else if (
                company_question?.company_question_conditions?.parents?.length >
                0
              ) {
                let filteredCQC =
                  company_question.company_question_conditions.parents.filter(
                    (cqc) =>
                      cqc.is_by_evaluation_js == false &&
                      cqc.is_required == true
                  );
                if (filteredCQC?.length > 0) {
                  requiredQuestions[company_question.id] = false;
                }
              } else {
                requiredQuestions[company_question.id] = false;
              }
            }
          });
          required[section.order] = requiredQuestions;
          section_order.push(section.order);
        });
        this.required = required;
        this.$store.commit('questions/setSectionOrder', section_order);
        // debugger
        // const find = document.querySelector('input');
        // setTimeout(() => {
        //   find.focus();
        // }, 0);
      }
    },
    requiredCompleted: {
      deep: true,
      handler: async function (nv) {
        let self = this;
        if (self.stepName == 'Questions') {
          if (nv == true) {
            await self.$store.commit('toggleDisabledButton', {
              bool: false,
              source: 'QuestionsStepOne #2',
            });
          } else {
            await self.$store.commit('toggleDisabledButton', {
              bool: true,
              source: 'QuestionsStepOne #3',
            });
          }
        }
      },
      immediate: true,
    },
    // required: {
    //   handler(newValue) {
    //     let self = this;
    //     if (newValue?.length > 0) {
    //       if (self.requiredCompleted == true) {
    //         self.$store.commit("toggleDisabledButton", false)
    //       } else {
    //         self.$store.commit("toggleDisabledButton", true)
    //       }
    //     }
    //   },
    //   immediate:true
    // }
    'this.$store.state.submitButtonDisabled': {
      handler() {
        let self = this;
        console.log(
          'submitButton toggled: ' + self.$store.state.submitButtonDisabled
        );
        // setTimeout(function() {
        //   console.log('post timeout submitButton toggled: '+self.$store.state.submitButtonDisabled);
        // }, 1500);
      },
      immediate: true,
    },
    // hasAnswers: {
    //   handler: async function(nv) {
    //     let self = this;
    //     if (nv && self.requiredCompleted) {
    //       await self.$store.commit("toggleDisabledButton", { bool: false, source: 'QuestionsStepOne #4' });
    //     } else {
    //       await self.$store.commit("toggleDisabledButton", { bool: true, source: 'QuestionsStepOne #5' });
    //     }
    //   },
    // },
    orderedSection: {
      deep: true,
      handler: async function (nv) {
        let self = this;
        if (nv?.length > 0 && self.init_hidden == false) {
          self.initHidden(nv);
          self.init_hidden = true;
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.$store.dispatch('questions/checkIfAnswersPresent');
  },
  methods: {
    order(array, orderBy) {
      return _.orderBy(array, orderBy);
    },
    toggleRequired(question_id, state) {
      console.log('togglingRequired');
      if (
        this.required[this.sectionOrder[this.currentSectionIndex]] &&
        this.required[this.sectionOrder[this.currentSectionIndex]][
          question_id
        ] !== undefined
      ) {
        this.required[this.sectionOrder[this.currentSectionIndex]][
          question_id
        ] = state;
      }
    },
    initHidden() {
      let self = this;
      self.data.company_question_sections.forEach((el) => {
        el.company_questions.forEach((company_question) => {
          if (
            company_question?.company_question_conditions?.parents?.length > 0
          ) {
            let filteredCQC =
              company_question.company_question_conditions.parents.filter(
                (cqc) => cqc.is_by_evaluation_js == false
              );
            if (
              filteredCQC?.length == 0 &&
              !self.hidden.includes(company_question.id)
            ) {
              self.hidden.push(company_question.id);
            }
          }
        });
      });
    },
    hideHidden(question_id) {
      let index = this.hidden.indexOf(question_id);
      if (index == -1) {
        this.hidden.push(question_id);
      }
    },
    unhideHidden(question_id) {
      let index = this.hidden.indexOf(question_id);
      if (index > -1) {
        this.hidden.splice(index, 1);
      }
    },
  },
};
</script>
